<script setup lang="ts">
import { T } from '@tolgee/vue'
import { computed, ref, watch } from 'vue'
import { type FortuneWheelPrizeV2 } from '@/types/shared/fortune-wheel'
import BalanceCurrency from '@/assets/images/dropee-token-new.webp'
import FeatherImage from '@/assets/images/feather.png'
import IdeaImage from '@/assets/images/special-currencies/idea.webp'
import HorseSpiritImage from '@/assets/images/special-currencies/horse-spirit.webp'
import FirecrackerImage from '@/assets/images/special-currencies/firecracker.webp'
import DropeeImage from '@/assets/images/dropee-token-new.webp'
import StarImage from '@/assets/images/premium.png'
import { formatNumberFullTruncate } from '@/helpers/utils'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { AD_TYPE, useAd } from '@/composables/game/ad'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import BaseButton from '@/components/base/BaseButton.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { DateTime } from 'luxon'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { usePlayer } from '@/composables/game/player'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'

const props = withDefaults(defineProps<{
	show: boolean
	prize: FortuneWheelPrizeV2
	hasSpinMultiplier: boolean
	appliedMultiplier?: number
}>(), {
  appliedMultiplier: 1,
})

const emit = defineEmits<{
	(e: 'adWatched'): void
	(e: 'ctaClick'): void
	(e: 'isLoadingAd', isLoading: boolean): void
}>()

useWatchOverlays(computed(() => props.show))
const { topStyle } = useFullScreenDimensions()
const { goldenCoinValue, isBasePlatform } = usePlayer()

const isClickable = ref(false)

const isCoin = computed(() => props.prize.currency === 'points')
const isGoldenCoin = computed(() => props.prize.currency === 'goldenCoin')
const isUsdt = computed(() => props.prize.currency === 'usdt')
const isFeather = computed(() => props.prize.currency === 'goldFeather')
const isIdea = computed(() => props.prize.currency === 'idea')
const isHorseSpirit = computed(() => props.prize.currency === 'horseSpirit')
const isFirecracker = computed(() => props.prize.currency === 'firecracker')
const isDropee = computed(() => props.prize.currency === 'dropee')
const isStar = computed(() => props.prize.currency === 'star')
const isHidden = ref(false)
const isLoadingAd = ref(false)

const prizeImage = computed(() => {
  if (isUsdt.value) return BalanceCurrency
  if (isFeather.value) return FeatherImage
  if (isIdea.value) return IdeaImage
  if (isHorseSpirit.value) return HorseSpiritImage
  if (isFirecracker.value) return FirecrackerImage
  if (isDropee.value) return DropeeImage
  if (isStar.value) return StarImage
  return allSpecialCurrenciesConfig.value[props.prize.currency]?.image ?? null
})

const {
  showAd,
  claimed,
  watchedDailyAdsCount,
  maxDailyAdsCount,
  countdownTimeBetweenAds,
  showAdsCountdown,
} = useAd(AD_TYPE.FOR_DOUBLE_PRIZE)
const remainingAdsCount = computed(() => Math.max(0, maxDailyAdsCount.value - watchedDailyAdsCount.value))

const showWatchAdSection = computed(() => !isBasePlatform.value && (isCoin.value || isGoldenCoin.value) && !claimed.value && props.prize.amount && !props.hasSpinMultiplier && props.appliedMultiplier <= 1)

async function watchAd() {
  if (showAdsCountdown.value) return
  isLoadingAd.value = true
  try {
    const watched = await showAd()
    isHidden.value = true
    if (watched) {
      emit('adWatched')
    }
  } finally {
    isLoadingAd.value = false
    setTimeout(() => onContainerClick(), 100)
  }
}

const { addHapticFeedback } = useHapticFeedback()

function onContainerClick() {
  if (isClickable.value) {
    addHapticFeedback('light')
    emit('ctaClick')
  }
}

watch(() => props.show, show => {
  if (show) {
    isHidden.value = false
    setTimeout(() => isClickable.value = true, 500)
  } else {
    isClickable.value = false
  }
}, { immediate: true })

watch(isLoadingAd, isLoadingAd => {
  emit('isLoadingAd', isLoadingAd)
  const appWrapper = document.querySelector('#app-wrapper')

  if (isLoadingAd) {
    if (appWrapper) {
      appWrapper.classList.add('!overflow-hidden')
      appWrapper.classList.add('no-overscroll')
    }
  } else {
    if (appWrapper) {
      appWrapper.classList.remove('!overflow-hidden')
      appWrapper.classList.remove('no-overscroll')
    }
  }
})
</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.95] flex flex-col z-[800]"
      :class="{'invisible': isHidden}"
      @click="onContainerClick"
    >
      <CrossCircleIcon
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div class="h-full w-full flex-1 flex flex-col gap-y-6 items-center justify-center relative">
        <div
          class="absolute w-[334px] h-[334px] rounded-full bg-teal-primary/[.54] blur-[43px] -z-[1]"
        />
        <h1 class="text-white text-[36px] font-bold leading-[42px] text-center">
          <T
            key-name="fortune_wheel.congratulations"
            default-value="Congratulations!"
          />
        </h1>
        <img
          src="@/assets/images/like.webp"
          class="size-[195px]"
        >
        <div class="flex items-center justify-center flex-wrap gap-x-3 uppercase">
          <div class="text-light-white text-[28px] leading-8 font-bold -tracking-[0.56px]">
            <T
              key-name="fortune_wheel.prize_modal.you_won"
              default-value="You won"
            />
          </div>
          <div
            class="flex items-center gap-x-2"
            :class="{'justify-center basis-full': isDropee}"
          >
            <img
              v-if="isCoin || isGoldenCoin"
              class="object-contain w-auto h-8"
              :src="CoinIcon"
            >
            <img
              v-else-if="prizeImage"
              class="object-contain w-6 h-6 min-w-[24px]"
              :src="prizeImage"
            >
            <div
              class="text-teal-primary text-[28px] leading-8 font-bold -tracking-[0.56px]"
            >
              <span v-if="isUsdt">
                {{ formatNumberFullTruncate((prize.amount ? prize.amount : 1) / 100, true) }} {{ ' ' }} USDT
              </span>
              <span v-else-if="isGoldenCoin">{{ formatNumberFullTruncate(prize.amount * goldenCoinValue) }}</span>
              <span v-else-if="isDropee">{{ formatNumberFullTruncate(prize.amount / 100) }} $DROPEE</span>
              <span v-else>{{ formatNumberFullTruncate(prize.amount) }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="appliedMultiplier > 1"
          class="text-white/[.87] text-xs font-bold -mt-3 text-center"
          v-html="isDropee ? `Multiplier only applies to in-game rewards.<br/>Only 1 spin was spent.` : `x ${appliedMultiplier} Multiplier applied`"
        />
        <div
          v-if="showWatchAdSection && prize"
          class="mt-6 flex items-center flex-col gap-y-3"
        >
          <div @click.stop.prevent="watchAd">
            <BaseButton
              :loading="isLoadingAd"
              :disabled="showAdsCountdown"
              class="min-w-[286px]"
            >
              <template #leftIcon>
                <img
                  src="@/assets/images/movie.png"
                  class="h-full w-auto max-w-[24px]"
                >
              </template>
              <span class="flex items-center gap-x-2">
                <span
                  v-if="showAdsCountdown"
                >
                  <T
                    key-name="tasks.ads.wait"
                    default-value="Wait"
                  />
                  <span>{{ ' ' }}</span>
                  <span>{{ DateTime.fromSeconds(countdownTimeBetweenAds).toFormat('mm:ss') }}</span>
                  <span>{{ ' ' }}</span>
                  <T
                    key-name="tasks.ads.for"
                    default-value="for"
                  />
                </span>
                <T
                  v-else
                  key-name="fortune_wheel.watch_an_ad_for"
                  default-value="Watch an ad for"
                />
                <span class="flex items-center gap-x-1">
                  <span>2x</span>
                  <img
                    :src="CoinIcon"
                    class="w-[15px] min-w-[15px] h-auto"
                    alt="Coin Icon"
                  >
                </span>
              </span>
            </BaseButton>
          </div>
          <div class="text-white flex items-center gap-x-2">
            <div
              class="flex items-center gap-x-1.5"
            >
              <img
                :src="CoinIcon"
                class="w-[15px] min-w-[15px] h-auto"
                alt="Coin Icon"
              >
              <div class="text-[21px] leading-6 font-bold -tracking-[0.42px]">
                {{ formatNumberFullTruncate(isGoldenCoin ? prize.amount * goldenCoinValue : prize.amount) }}
              </div>
            </div>
            <ArrowRightIcon class="size-8 min-w-[32px]" />
            <div
              class="flex items-center gap-x-1.5"
            >
              <img
                :src="CoinIcon"
                class="w-[15px] min-w-[15px] h-auto"
                alt="Coin Icon"
              >
              <div class="text-[21px] leading-6 font-bold -tracking-[0.42px]">
                {{ formatNumberFullTruncate(isGoldenCoin ? 2 * prize.amount * goldenCoinValue : 2 * prize.amount) }}
              </div>
            </div>
          </div>
          <div class="text-center text-xs font-medium text-white/[.87]">
            <T
              key-name="fortune_wheel.watch_ad_remaining_ads_count"
              default-value="You have {ad, plural, one {# ad} other {# ads}} left today"
              :params="{ad: remainingAdsCount}"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
	opacity: 0;
}
</style>
